import {
  ContractKind,
  OffCycleRule,
  ReferenceDateRule,
  Role,
  SubmissionCadence,
  TerminationDocumentPolicy,
  type CompanyResponse,
  type ContractCreate,
  type ContractResponse,
  type ContractUpdate,
} from '../../api';
import { ROLE_LABELS, USERS_COPY } from './staffUsers';

/**
 * The words the onboarding screens use, and the two rules that make a default
 * readable.
 *
 * A lookup module like [`offCycle`](./offCycle.ts) and
 * [`reviewQueue`](./reviewQueue.ts), and here for the same reason: the
 * off-cycle rule is named on the contract form, in the contract listing and in
 * the off-cycle panel of another screen, and "Só pelo arquivo mensal" spelled
 * two ways across them is how a screen stops being trusted. `RULE_LABELS` below
 * is deliberately the same wording `offCycle.ts` uses.
 *
 * The second rule is about defaults. `reference_date_rule` and `off_cycle_rule`
 * both have API defaults, and both are terms of the insurer's contract rather
 * than product settings -- `off_cycle_rule` defaults to `not_permitted` because
 * we do not assume an insurer accepts anything outside the monthly file until
 * somebody has asked them. A form that quietly applied that would be recording
 * an agreement nobody made. So the defaults are shown, labelled as defaults,
 * with the sentence that says what each one means.
 */

/** What each insurer takes between cutoffs. Same words the off-cycle panel uses. */
export const RULE_LABELS: Record<OffCycleRule, string> = {
  [OffCycleRule.NOT_PERMITTED]: 'Só pelo arquivo mensal',
  [OffCycleRule.ADMISSIONS_ONLY]: 'Inclusões urgentes fora do ciclo',
  [OffCycleRule.ALL_MOVEMENTS]: 'Qualquer movimentação fora do ciclo',
};

/** What each rule actually commits us to, in one sentence. */
export const RULE_MEANINGS: Record<OffCycleRule, string> = {
  [OffCycleRule.NOT_PERMITTED]:
    'A cobertura entra pelo arquivo mensal e por mais nada. Avisar antes não adianta, e o ' +
    'painel "Fora do ciclo" fica fechado neste contrato.',
  [OffCycleRule.ADMISSIONS_ONLY]:
    'A operadora aceita inclusão urgente entre os cortes. Exclusão e alteração salarial ' +
    'esperam o arquivo mensal.',
  [OffCycleRule.ALL_MOVEMENTS]:
    'Qualquer uma das três movimentações pode ser comunicada antes do corte.',
};

/** Which date within the cycle month the population is photographed at. */
export const REFERENCE_DATE_LABELS: Record<ReferenceDateRule, string> = {
  [ReferenceDateRule.CUTOFF_DATE]: 'No dia do corte',
  [ReferenceDateRule.COVERAGE_MONTH_START]: 'No primeiro dia do mês coberto',
  [ReferenceDateRule.COVERAGE_MONTH_END]: 'No último dia do mês coberto',
};

export const REFERENCE_DATE_MEANINGS: Record<ReferenceDateRule, string> = {
  [ReferenceDateRule.CUTOFF_DATE]:
    'A população é lida no dia de corte deste contrato. Quem sai depois do corte ainda ' +
    'aparece no arquivo do mês.',
  [ReferenceDateRule.COVERAGE_MONTH_START]:
    'A população é lida no dia 1º do mês coberto. Quem entra no meio do mês só aparece no ' +
    'arquivo seguinte.',
  [ReferenceDateRule.COVERAGE_MONTH_END]:
    'A população é lida no último dia do mês coberto. Quem sai no meio do mês não aparece ' +
    'naquele arquivo.',
};

/**
 * What insurance a contract is. Shown wherever a contract is named, because one
 * person can sit under a vida and a saúde contract of the same company.
 */
export const KIND_LABELS: Record<ContractKind, string> = {
  [ContractKind.VIDA]: 'Vida',
  [ContractKind.SAUDE]: 'Saúde',
};

export const CADENCE_LABELS: Record<SubmissionCadence, string> = {
  [SubmissionCadence.MONTHLY]: 'Mensal',
};

/**
 * The defaults the API applies when a field is omitted.
 *
 * Duplicated from the generated field docs on purpose: the form needs to *show*
 * them before the request is made, and a default nobody can see until after the
 * fact is not a default a person agreed to.
 */
export const CONTRACT_DEFAULTS = {
  kind: ContractKind.VIDA,
  submission_cadence: SubmissionCadence.MONTHLY,
  reference_date_rule: ReferenceDateRule.CUTOFF_DATE,
  off_cycle_rule: OffCycleRule.NOT_PERMITTED,
} as const;

export const ONBOARDING_COPY = {
  title: 'Empresas clientes',
  description:
    'As empresas atendidas pela vyttech. Abrir uma mostra os contratos dela; as operadoras que ' +
    'esses contratos apontam ficam na tela ao lado.',

  // --- Companies -----------------------------------------------------------
  registerCompanyTitle: 'Cadastrar empresa',
  registerCompanyDescription:
    'A empresa é o cliente: todo beneficiário, contrato e movimentação pertence a uma. ' +
    'Cadastrar é o primeiro passo e não depende de mais nada.',
  companyNameLabel: 'Razão social',
  companyNameHint: 'Como a empresa aparece nas telas e nos relatórios.',
  companySlugLabel: 'Identificador',
  companyCnpjLabel: 'CNPJ',
  companyCnpjHint:
    'Com ou sem pontuação — "12.345.678/0001-95" e "12345678000195" são o mesmo número. É por ' +
    'ele que o eSocial e as operadoras identificam o empregador, então é único entre todas as ' +
    'empresas. Desde julho de 2026 a Receita emite CNPJ com letras ("12.ABC.345/01DE-35"); ' +
    'esse também vale.',
  companyCnpjMissing: 'Sem CNPJ',
  companyCnpjMissingHint:
    'Esta empresa foi cadastrada antes do campo existir. Informe o CNPJ abaixo para que ela ' +
    'apareça corretamente nos arquivos e nas integrações.',
  registerCompany: 'Cadastrar empresa',
  registeringCompany: 'Cadastrando…',
  companyRegistered: 'Empresa cadastrada.',
  companiesTitle: 'Empresas cadastradas',
  companiesDescription: 'Abra uma para ver e manter os contratos dela.',
  companiesLoading: 'Carregando empresas…',
  companiesError: 'Não foi possível carregar as empresas',
  noCompanies: 'Nenhuma empresa cadastrada',
  noCompaniesDescription:
    'Cadastre a primeira em "Cadastrar empresa". Sem empresa não há contrato, e sem contrato ' +
    'não há cadastro de beneficiários.',
  companiesCaption: 'Empresas clientes cadastradas',
  // The filter row and the sort of the list (#439). A filter that leaves
  // nothing keeps the table, so its inputs stay where they can be changed, and
  // says so under them. Its way out has its own words, because the table's
  // toolbar already has a "Limpar filtros" that only empties the inputs.
  companyCreatedAtLabel: 'Cadastrada em',
  companiesFilterPlaceholder: 'Filtrar',
  noMatchingFilteredCompanies: 'Nenhuma empresa corresponde aos filtros',
  noMatchingFilteredCompaniesDescription:
    'Nenhuma empresa atende a tudo o que foi pedido nas colunas. Altere um filtro ou limpe ' +
    'todos.',
  clearCompaniesFilters: 'Mostrar todas as empresas',
  // A page past the end: an old link after the list shrank, or a typed number.
  companiesPageOutOfRange: 'Esta página não existe mais',
  companiesPageOutOfRangeDescription: 'A lista tem menos páginas do que o endereço pede.',
  companiesFirstPage: 'Ir para a primeira página',
  back: 'Voltar para a lista de empresas',
  // The way out of a creation screen without creating anything. Its own word
  // rather than the browser's back button, because a form somebody has typed
  // into needs to say that leaving it is allowed and loses nothing.
  cancel: 'Cancelar',
  backToInsurers: 'Voltar para a lista de operadoras',
  backToCompany: 'Voltar para a empresa',

  // --- The CNPJ of a company that predates the field -----------------------
  cnpjPanelTitle: 'CNPJ da empresa',
  cnpjPanelDescription:
    'O número que identifica o empregador para a Receita, o eSocial e as operadoras. Informe ' +
    'o de uma empresa cadastrada antes do campo existir, ou corrija um que foi registrado ' +
    'errado. Toda alteração fica na trilha de auditoria, com quem fez e quando.',
  saveCnpj: 'Salvar CNPJ',
  savingCnpj: 'Salvando…',
  cnpjSaved: 'CNPJ atualizado.',
  correctCnpj: 'Corrigir CNPJ',
  cancelCnpj: 'Cancelar',

  // --- The name insurer files carry ----------------------------------------
  // The registered name stays what every screen shows; this only changes the
  // company part of a file name, so "ACTEGA" can go out as "Actega_…xlsx".
  fileNameLabelTitle: 'Nome nos arquivos da seguradora',
  fileNameLabelDescription:
    'Como a empresa aparece no nome dos arquivos enviados à seguradora, no lugar da razão ' +
    'social. A razão social continua a mesma em todas as telas. Vale para os arquivos gerados ' +
    'daqui em diante; os já gerados mantêm o nome que têm.',
  fileNameLabelGroupDescription:
    'Como as empresas deste grupo aparecem no nome dos arquivos enviados à seguradora. As ' +
    'empresas sem um nome próprio usam este; uma empresa com nome próprio continua com o dela. ' +
    'Vale para os arquivos gerados daqui em diante.',
  fileNameLabelLabel: 'Nome nos arquivos',
  fileNameLabelHint:
    'Exatamente como deve aparecer: maiúsculas e minúsculas são mantidas, e cada espaço vira ' +
    '"_". Só letras, números, espaço, "_" e "-", até 100 caracteres.',
  fileNameLabelNone: 'Nenhum nome definido',
  fileNameLabelSave: 'Salvar nome',
  fileNameLabelSaving: 'Salvando…',
  fileNameLabelSaved: 'Nome nos arquivos atualizado.',
  fileNameLabelClear: 'Remover nome',
  fileNameLabelCleared: 'Nome nos arquivos removido.',
  fileNameLabelUnchanged: 'O nome é o mesmo de antes. Altere-o antes de salvar.',
  fileNameLabelBlank: 'Informe um nome. Para deixar de usar um, clique em "Remover nome".',
  fileNameLabelRejected:
    'Use só letras, números, espaço, "_" e "-", até 100 caracteres: nada do que for digitado ' +
    'pode sumir do nome do arquivo.',
  fileNameLabelInForceOwn: 'Em uso nos arquivos: o nome próprio desta empresa',
  fileNameLabelInForceGroup: 'Em uso nos arquivos: o nome do grupo econômico',
  fileNameLabelInForceRegistered: 'Em uso nos arquivos: a razão social',

  // --- What the review does about an undocumented termination --------------
  // Some customers must not have a termination approved without the rescisão
  // paperwork, some only want the analyst told, and some never send any.
  terminationPolicyTitle: 'Documento de rescisão',
  terminationPolicyDescription:
    'O que a análise faz com um desligamento sem documento comprobatório anexado. Vale para ' +
    'todos os contratos desta empresa, na próxima vez que a fila for aberta. Toda alteração ' +
    'fica na trilha de auditoria, com quem fez e quando.',
  terminationPolicyLabel: 'Documento de rescisão',
  terminationPolicyCurrent: 'Em vigor',
  terminationPolicySave: 'Salvar',
  terminationPolicySaving: 'Salvando…',
  terminationPolicySaved: 'Política de documento de rescisão atualizada.',
  terminationPolicyUnchanged: 'Essa já é a opção em vigor. Escolha outra antes de salvar.',

  // --- Insurers ------------------------------------------------------------
  insurersTitle: 'Operadoras',
  insurersDescription:
    'As operadoras para quem enviamos arquivos. Não pertencem a nenhuma empresa cliente: uma ' +
    'só é cadastrada aqui e todo contrato escolhe uma delas.',
  insurerCodeLabel: 'Código',
  insurerCodeHint: 'Identificador curto e estável. Guardado em maiúsculas e único.',
  insurerNameLabel: 'Nome',
  insurerLayoutLabel: 'Código do layout',
  insurerLayoutHint:
    'O layout padrão é o que toda operadora para quem enviamos hoje usa. Só escolha outro se ' +
    'esta operadora tiver um layout registrado só dela.',
  layoutsError:
    'Não foi possível carregar os layouts registrados. Ainda dá para cadastrar a operadora ' +
    'com o layout padrão.',
  registerInsurer: 'Cadastrar operadora',
  registeringInsurer: 'Cadastrando…',
  insurerRegistered: 'Operadora cadastrada.',
  insurersLoading: 'Carregando operadoras…',
  insurersError: 'Não foi possível carregar as operadoras',
  noInsurers: 'Nenhuma operadora cadastrada',
  noInsurersDescription:
    'Um contrato precisa de uma operadora, então cadastre a primeira em "Cadastrar operadora".',
  insurersCaption: 'Operadoras cadastradas',
  defaultLayout: 'Layout padrão',
  retired: 'Inativa',
  active: 'Ativa',

  // --- Contracts -----------------------------------------------------------
  contractsTitle: 'Contratos',
  contractsDescription:
    'Um contrato por operadora e população. É ele que carrega os códigos da operadora e os ' +
    'termos do ciclo mensal.',
  contractsLoading: 'Carregando contratos…',
  contractsError: 'Não foi possível carregar os contratos',
  noContracts: 'Nenhum contrato nesta empresa',
  noContractsDescription:
    'Crie o primeiro em "Criar contrato". Sem contrato não há para onde importar a população ' +
    'nem o que enviar à operadora.',
  contractsCaption: 'Contratos da empresa',
  newContractTitle: 'Novo contrato',
  newContractDescription:
    'A empresa é esta, e vai no cabeçalho de escopo da requisição em vez de num campo do ' +
    'formulário: um contrato não tem como nomear uma empresa diferente da que está na tela.',
  editContractTitle: 'Editar contrato',
  editContractDescription:
    'Só o que você mudar é enviado. Campo não alterado fica como está — a API trata omissão ' +
    'como "não mexa".',
  insurerLabel: 'Operadora',
  insurerPlaceholder: 'Escolha uma operadora…',
  insurerHint: 'Para quem o arquivo mensal deste contrato vai.',
  kindLabel: 'Tipo',
  kindHint:
    'O seguro que este contrato é. Separa dois contratos da mesma empresa quando a mesma ' +
    'pessoa está nos dois; não muda nada no arquivo da operadora.',
  subestipulanteLabel: 'Subestipulante',
  subestipulanteHint:
    'O código que a operadora deu para esta empresa. Fica no contrato e em nenhum outro ' +
    'lugar — nunca é preenchido por pessoa. Número inteiro maior que zero.',
  moduloLabel: 'Módulo',
  moduloHint:
    'O código que a operadora deu para esta população dentro da empresa. Independente do ' +
    'subestipulante, mesmo quando os contratos de hoje usam os dois iguais.',
  // The policy's own references, as the insurer's file prints them (CONTRATO,
  // APÓLICE). Optional, and shared by every contract under the same
  // policy -- unlike the two codes above, which name this population.
  policyReferencesNote:
    'Os dois campos abaixo são as referências da apólice no arquivo da operadora. São ' +
    'opcionais e podem se repetir em outros contratos, até de outras empresas, sob a mesma ' +
    'apólice. Deixe em branco o que não tiver.',
  insurerContractNumberLabel: 'Número do contrato',
  insurerContractNumberHint:
    'O CONTRATO no arquivo da operadora. Só dígitos, até 20, sem ponto nem traço; zero à ' +
    'esquerda é mantido.',
  apoliceLabel: 'Apólice',
  apoliceHint: 'A APÓLICE no arquivo da operadora. Só dígitos, até 20, sem ponto nem traço.',
  policyReferencesHeader: 'Contrato / apólice',
  cadenceLabel: 'Periodicidade do envio',
  cadenceHint: 'Com que frequência o arquivo de movimentação vai para a operadora.',
  cutoffDayLabel: 'Dia do corte',
  cutoffDayHint:
    'Dia do mês em que o período fecha, de 1 a 31. Movimentação registrada depois dele entra ' +
    'no ciclo seguinte. Dia que não existe num mês curto vira o último dia daquele mês.',
  referenceDateLabel: 'Data de referência da população',
  offCycleLabel: 'Fora do ciclo',
  createContract: 'Criar contrato',
  creatingContract: 'Criando…',
  contractCreated: 'Contrato criado.',
  saveContract: 'Salvar alterações',
  savingContract: 'Salvando…',
  contractSaved: 'Contrato atualizado.',
  cancelEdit: 'Cancelar',
  edit: 'Editar',
  nothingChanged: 'Nada mudou. Altere algum campo antes de salvar.',
  defaultSuffix: '(padrão da API)',
  termsNote:
    'Os dois campos abaixo são termos do contrato com a operadora, não preferências nossas. ' +
    'Eles já vêm preenchidos com o padrão da API e o que cada padrão significa está escrito ' +
    'ao lado — mudar o que o produto faz com eles é registrar aqui o que foi acordado, não ' +
    'publicar uma versão.',
  positiveInteger: 'Informe um número inteiro maior que zero.',
  cutoffOutOfRange: 'Informe um dia entre 1 e 31.',

  // The baseline population is loaded per contract rather than per company --
  // two contracts are two populations -- so the way in is the contract's own
  // row. See [`BaselineImportPage`](./BaselineImportPage.tsx).
  baselineAction: 'Importar população',

  // --- A bookmarked company that is no longer there ------------------------
  //
  // The detail view is reachable by its own address, so it is reachable by an
  // address that is stale, mistyped or belongs to somebody else's tenant. It
  // says so and offers the way back instead of rendering an empty shell.
  companyNotFound: 'Empresa não encontrada',
  companyNotFoundDescription:
    'Este endereço aponta para uma empresa que não existe mais, ou que esta conta não pode ' +
    'ver. Volte para a lista e abra uma de lá.',

  // --- The people of this company ------------------------------------------
  //
  // The company detail's own slice of #256: the list next door
  // (`StaffUsersPage`) answers "who has an account anywhere?"; this one
  // answers "who from *this* company signs in?", without a filter to pick and
  // with no way to create somebody in another company.
  usersTitle: 'Usuários da empresa',
  usersDescription:
    'Quem do RH desta empresa entra no portal, com qual permissão, e como um acesso é ' +
    'encerrado. Desativar não é apagar: a conta para de entrar e continua na lista, marcada.',
  usersLoading: 'Carregando pessoas…',
  usersError: 'Não foi possível carregar as pessoas',
  noUsers: 'Nenhuma pessoa desta empresa',
  noUsersDescription:
    'Cadastre a primeira em "Cadastrar pessoa". Sem conta, o RH desta empresa não entra no ' +
    'portal dela.',
  usersCaption: 'Pessoas desta empresa que entram no produto',
  // Read out of `staffUsers` rather than respelled: cadastrar uma pessoa aqui
  // and cadastrar uma pessoa na lista da equipe are the same act, and the
  // warning about the initial password is the same warning. Two copies of a
  // sentence are two sentences as soon as somebody edits one of them.
  addUser: USERS_COPY.addPerson,
  addUserDescription: USERS_COPY.addPersonDescription,
  addingUser: USERS_COPY.adding,
  cancelUser: USERS_COPY.cancel,
  // Read out of `staffUsers` for the same reason the sentences above are: a
  // person is asked for the same four things here and on the staff-wide form,
  // and a row of people is read with the same words here and on the staff-wide
  // list. Only `actionsLabel` is this file's own, because "Ações" is a column
  // of the contracts table too and belongs to no vocabulary in particular.
  userFullNameLabel: USERS_COPY.fullNameLabel,
  userEmailLabel: USERS_COPY.emailLabel,
  userEmailHint: USERS_COPY.emailHint,
  userPasswordLabel: USERS_COPY.passwordLabel,
  userPasswordHint: USERS_COPY.passwordHint,
  userRoleLabel: USERS_COPY.profileLabel,
  userNameLabel: USERS_COPY.nameLabel,
  userStatusLabel: USERS_COPY.statusLabel,
  userActive: USERS_COPY.active,
  userInactive: USERS_COPY.inactive,
  actionsLabel: 'Ações',
  deactivateUser: USERS_COPY.deactivate,
  deactivatedUser: USERS_COPY.deactivated,
  deactivateRefused: USERS_COPY.deactivateRefused,
  // The company is not a field on this form: the address already carries it,
  // and the request goes out with the scope header rather than a body field --
  // the same reason the contract form does not offer it.
  userCompanyNote:
    'Esta pessoa passa a ver o portal desta empresa e de mais nenhuma. Equipe vyttech se cadastra ' +
    'pela lista de usuários, não por aqui.',
} as const;

/** The minimum and maximum the API accepts for `cutoff_day`. */
export const MIN_CUTOFF_DAY = 1;
export const MAX_CUTOFF_DAY = 31;

/**
 * A contract as the form holds it: every field a string, because that is what
 * an input gives back.
 */
export interface ContractFormState {
  readonly insurerId: string;
  readonly kind: ContractKind;
  readonly subestipulante: string;
  readonly modulo: string;
  readonly insurerContractNumber: string;
  readonly apolice: string;
  readonly cadence: SubmissionCadence;
  readonly cutoffDay: string;
  readonly referenceDateRule: ReferenceDateRule;
  readonly offCycleRule: OffCycleRule;
}

/**
 * A blank contract form, sitting on the API's own defaults.
 *
 * The defaults are *in the form*, visible and changeable, rather than left to
 * the server by omission. See the module docstring.
 */
export const EMPTY_CONTRACT_FORM: ContractFormState = {
  insurerId: '',
  kind: CONTRACT_DEFAULTS.kind,
  subestipulante: '',
  modulo: '',
  insurerContractNumber: '',
  apolice: '',
  cadence: CONTRACT_DEFAULTS.submission_cadence,
  cutoffDay: '',
  referenceDateRule: CONTRACT_DEFAULTS.reference_date_rule,
  offCycleRule: CONTRACT_DEFAULTS.off_cycle_rule,
};

/** An existing contract, as the edit form holds it. */
export function contractFormOf(contract: ContractResponse): ContractFormState {
  return {
    insurerId: contract.insurer_id,
    kind: contract.kind,
    subestipulante: String(contract.subestipulante),
    modulo: String(contract.modulo),
    insurerContractNumber: contract.insurer_contract_number ?? '',
    apolice: contract.apolice ?? '',
    cadence: contract.submission_cadence,
    cutoffDay: String(contract.cutoff_day),
    referenceDateRule: contract.reference_date_rule,
    offCycleRule: contract.off_cycle_rule,
  };
}

/**
 * A person as the company detail's form holds them: every field a string,
 * because that is what an input gives back.
 */
export interface UserFormState {
  readonly fullName: string;
  readonly email: string;
  readonly password: string;
  readonly role: CompanyUserRole;
}

/**
 * The two roles a person of *this* company can hold.
 *
 * Deliberately narrower than `HUMAN_ROLES` in
 * [`staffUsers`](./staffUsers.ts): a vyttech role belongs to no company, and a
 * form already inside one company has no way to create outside it -- offering
 * the roles would be offering a `403`. The staff-wide list
 * ([`StaffUsersPage`](./StaffUsersPage.tsx)) is where vyttech people are
 * created.
 *
 * A narrower list, not a second vocabulary. The label and the meaning are read
 * out of `staffUsers`, never respelled here: that module exists precisely so a
 * role is not called one thing on one screen and another on the next, and a
 * copy of the words would go on passing its own tests while drifting away from
 * the screen next door.
 */
export const COMPANY_USER_ROLES = [Role.CUSTOMER_ADMIN, Role.CUSTOMER_USER] as const;

export type CompanyUserRole = (typeof COMPANY_USER_ROLES)[number];

export const USER_ROLE_OPTIONS = COMPANY_USER_ROLES.map((role) => ({
  value: role,
  label: ROLE_LABELS[role],
}));

/** A blank person form. `customer_user` is the humblest choice, so it is the default. */
export const EMPTY_USER_FORM: UserFormState = {
  fullName: '',
  email: '',
  password: '',
  role: Role.CUSTOMER_USER,
};

export type ContractFormErrors = Record<string, string>;

/**
 * What the browser can refuse before the server has to.
 *
 * Only the two things a `<select>` and a required attribute cannot express: a
 * positive integer, and a day of the month. Everything else -- an unknown
 * insurer, a duplicate contract, a contract number or apólice that is not all
 * digits -- is the server's judgement and is rendered from its response,
 * because a second copy of a rule here is a second copy that goes stale.
 */
export function validateContractForm(form: ContractFormState): ContractFormErrors {
  const errors: ContractFormErrors = {};
  if (!isPositiveInteger(form.subestipulante)) {
    errors['subestipulante'] = ONBOARDING_COPY.positiveInteger;
  }
  if (!isPositiveInteger(form.modulo)) {
    errors['modulo'] = ONBOARDING_COPY.positiveInteger;
  }
  const cutoff = Number(form.cutoffDay);
  if (!isPositiveInteger(form.cutoffDay) || cutoff < MIN_CUTOFF_DAY || cutoff > MAX_CUTOFF_DAY) {
    errors['cutoff_day'] = ONBOARDING_COPY.cutoffOutOfRange;
  }
  return errors;
}

function isPositiveInteger(value: string): boolean {
  return /^\d+$/.test(value.trim()) && Number(value) > 0;
}

/** A policy reference as the API takes it: trimmed, and `null` when blank. */
function policyReference(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * Only the fields that actually moved, as `PATCH /contracts/{id}` takes them.
 *
 * The point of comparing rather than sending the whole form back: the API
 * treats an omitted field as "leave it alone", and honouring that means a
 * person who came to change the cutoff day cannot also rewrite the off-cycle
 * rule to whatever the form was holding when it opened. An empty object means
 * nothing changed, and the caller says so instead of making a pointless request.
 *
 * The two policy references are optional on a contract, so emptying one is a
 * change like any other: it goes out as `null`, which the API reads as "clear".
 */
export function contractChanges(
  original: ContractResponse,
  form: ContractFormState,
): ContractUpdate {
  const changes: ContractUpdate = {};
  if (form.insurerId !== original.insurer_id) {
    changes.insurer_id = form.insurerId;
  }
  if (form.kind !== original.kind) {
    changes.kind = form.kind;
  }
  if (Number(form.subestipulante) !== original.subestipulante) {
    changes.subestipulante = Number(form.subestipulante);
  }
  if (Number(form.modulo) !== original.modulo) {
    changes.modulo = Number(form.modulo);
  }
  const insurerContractNumber = policyReference(form.insurerContractNumber);
  if (insurerContractNumber !== original.insurer_contract_number) {
    changes.insurer_contract_number = insurerContractNumber;
  }
  const apolice = policyReference(form.apolice);
  if (apolice !== original.apolice) {
    changes.apolice = apolice;
  }
  if (form.cadence !== original.submission_cadence) {
    changes.submission_cadence = form.cadence;
  }
  if (Number(form.cutoffDay) !== original.cutoff_day) {
    changes.cutoff_day = Number(form.cutoffDay);
  }
  if (form.referenceDateRule !== original.reference_date_rule) {
    changes.reference_date_rule = form.referenceDateRule;
  }
  if (form.offCycleRule !== original.off_cycle_rule) {
    changes.off_cycle_rule = form.offCycleRule;
  }
  return changes;
}

/**
 * The body `POST /contracts` takes. The company is not in it, and cannot be.
 *
 * A blank policy reference is left out rather than sent as `null`: on a create
 * the two mean the same, and the smaller body is the one that says so.
 */
export function contractCreateBody(form: ContractFormState): ContractCreate {
  const insurerContractNumber = policyReference(form.insurerContractNumber);
  const apolice = policyReference(form.apolice);
  return {
    insurer_id: form.insurerId,
    kind: form.kind,
    subestipulante: Number(form.subestipulante),
    modulo: Number(form.modulo),
    ...(insurerContractNumber !== null && { insurer_contract_number: insurerContractNumber }),
    ...(apolice !== null && { apolice }),
    submission_cadence: form.cadence,
    cutoff_day: Number(form.cutoffDay),
    reference_date_rule: form.referenceDateRule,
    off_cycle_rule: form.offCycleRule,
  };
}

/** The options a `SelectField` takes, with the default one marked as such. */
export function optionsWithDefault<TValue extends string>(
  labels: Record<TValue, string>,
  defaultValue: TValue,
): readonly { readonly value: TValue; readonly label: string }[] {
  return (Object.keys(labels) as TValue[]).map((value) => ({
    value,
    label:
      value === defaultValue ? `${labels[value]} ${ONBOARDING_COPY.defaultSuffix}` : labels[value],
  }));
}

/** Where the name a company's insurer files carry comes from. */
export type FileNameLabelSource = 'own' | 'group' | 'registered';

/**
 * Read off the two fields the server resolves, never worked out again here: a
 * label of the company's own wins, then the group's, then the registered name.
 * Fixtures and older payloads may omit the fields; both mean "none set".
 */
export function fileNameLabelSource(company: CompanyResponse): FileNameLabelSource {
  if ((company.file_name_label ?? null) !== null) {
    return 'own';
  }
  return (company.file_name_label_in_force ?? null) !== null ? 'group' : 'registered';
}

/** The three termination-document policies, in the words the staff screens use. */
export const TERMINATION_DOCUMENT_POLICY_LABELS: Record<TerminationDocumentPolicy, string> = {
  [TerminationDocumentPolicy.BLOCK]: 'Impedir aprovação sem documento',
  [TerminationDocumentPolicy.WARN]: 'Apenas avisar',
  [TerminationDocumentPolicy.OFF]: 'Não verificar',
};

/** What each one does, said under the select so nobody has to guess. */
export const TERMINATION_DOCUMENT_POLICY_HINTS: Record<TerminationDocumentPolicy, string> = {
  [TerminationDocumentPolicy.BLOCK]:
    'Um desligamento sem documento fica bloqueado: não pode ser aprovado, nem o ciclo dele, ' +
    'até que um documento seja anexado.',
  [TerminationDocumentPolicy.WARN]:
    'Um desligamento sem documento aparece com um aviso, e o analista decide se aprova.',
  [TerminationDocumentPolicy.OFF]:
    'Desligamentos sem documento não são sinalizados. Para clientes que não enviam documentos ' +
    'de rescisão.',
};

export const TERMINATION_DOCUMENT_POLICY_OPTIONS = [
  TerminationDocumentPolicy.BLOCK,
  TerminationDocumentPolicy.WARN,
  TerminationDocumentPolicy.OFF,
].map((value) => ({ value, label: TERMINATION_DOCUMENT_POLICY_LABELS[value] }));
